import React, { useState, useEffect } from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import ReactRouterPropTypes from 'react-router-prop-types';
import RecordOccurrencesForm from './Form';
import SmartCard from '../../../shared/components/SmartCard';
import { updatePendency } from '../../../shared/utils/smartutils';
import {
  changeLoadingFalse,
  changeLoadingTrue,
} from '../../../redux/actions/loadingActions';
import GraphQL from '../../../GraphQL';
import { showNotificationAutoDestroy } from '../../../shared/components/ShowNotification';
import {
  findByCommunicationIdQuery,
  findByPlanHeaderIdQuery,
} from '../../../shared/queries/ClassPlan';
import { subjectListAllQuery } from '../../../shared/queries/Subject';
import listBncc from '../../../shared/queries/Bncc';
import { relationObjectListBySchoolClassQuery } from '../../../shared/queries/RelationObject';
import { communicationSaveMutation } from '../../../shared/queries/Communication';
import { AVAILABLE_TO } from '../../../shared/utils/constants';

const ClassPlanFormHandler = (props) => {
  const { t, match, history, dispatch } = props;
  const [dto, setDto] = useState({
    id: 0,
    editable: true,
    rangeDate: {
      initialDate: new Date(),
      finalDate: new Date(),
    },
    planHeader: {
      id: 0,
      rangeDate: {},
    },
    visibleTo: AVAILABLE_TO.ONLY_RESPONSIBLES,
    htmlContent: '',
  });
  const [formOn, setFormOn] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [bnccs, setBnccs] = useState([]);
  const [type, setType] = useState('PLAN');

  const showLoading = () => {
    dispatch(changeLoadingTrue());
  };

  const hideLoading = () => {
    dispatch(changeLoadingFalse());
  };

  const backClickHandler = () => {
    history.goBack();
  };

  const createDto = (res, id) => {
    const updatedDto = {};
    updatedDto.planHeader = { ...res };
    updatedDto.id = parseInt(id, 0);
    return updatedDto;
  };

  const updateDto = (res, value) => {
    const updatedDto = { ...value };
    const bnccSkills = res.map((item) => ({
      label: item.bnccSkill.name,
      value: item.bnccSkill.id,
      description: item.bnccSkill.description,
    }));
    updatedDto.planHeader.rangeDate = {};
    updatedDto.planHeader.rangeDate.initialDate =
      updatedDto.planHeader.startDate;
    updatedDto.planHeader.rangeDate.finalDate = updatedDto.planHeader.endDate;
    updatedDto.planHeader.bnccSkills = bnccSkills;
    updatedDto.planHeader.subject.label = updatedDto.planHeader.subject.name;
    updatedDto.planHeader.subject.value = updatedDto.planHeader.subject.id;
    updatedDto.editable = true;
    updatedDto.visibleTo = AVAILABLE_TO.ONLY_RESPONSIBLES;
    updatedDto.htmlContent = '';
    return updatedDto;
  };

  const findById = async (id) => {
    let retorno;
    showLoading();
    const query = findByCommunicationIdQuery(id);
    await GraphQL(query, {}).then((res) => {
      hideLoading();
      retorno = createDto(res.findPlanHeaderByCommunicationId, id);
    });
    return retorno;
  };

  const findByBncc = (value) => {
    showLoading();
    const query = findByPlanHeaderIdQuery(value.planHeader.id);
    GraphQL(query, {}).then((res) => {
      hideLoading();
      const updatedDto = updateDto(res.findBnccSkillsByPlanHeader, value);
      setDto(updatedDto);
    });
  };
  const loadData = async (id) => {
    const planHeaderId = await findById(id);
    findByBncc(planHeaderId);
  };

  const loadAllBnccs = () => {
    showLoading();
    const query = listBncc();
    GraphQL(query, {})
      .then((res) => {
        hideLoading();
        res.listBncc = res.listBncc.map((item) => ({
          ...item,
          label: item.name,
          value: item.id,
        }));
        setBnccs(res.listBncc);
      })
      .catch(() => {
        hideLoading();
        showNotificationAutoDestroy(
          'danger',
          t('errors.title'),
          t('errors.error-to-loading')
        );
      });
  };

  const saveCommunication = (values) => {
    const communication = {
      ...values,
      type,
    };
    const mutation = communicationSaveMutation(communication);
    GraphQL(mutation, {
      htmlContent: values.planHeader.contents,
      textContent: values.planHeader.contents,
      title: values.planHeader.theme,
    })
      .then(() => {
        hideLoading();
        showNotificationAutoDestroy(
          'success',
          t('success.title'),
          t('success.successfully-saved')
        );
        backClickHandler();
      })
      .finally(() => {
        updatePendency(dispatch);
      });
  };

  const handleSubmit = (values) => {
    showLoading();
    saveCommunication(values);
  };

  const loadRecipients = () => {
    showLoading();
    const query = relationObjectListBySchoolClassQuery(false, false);
    GraphQL(query, {})
      .then((res) => {
        res.relationObjectListByParams = res.relationObjectListByParams.map(
          (item) => ({
            ...item,
            value: item.identifier,
            label: item.description,
          })
        );
        setRecipients(res.relationObjectListByParams);
        hideLoading();
      })
      .catch(() => {
        hideLoading();
        showNotificationAutoDestroy(
          'danger',
          t('errors.title'),
          t('errors.error-to-loading')
        );
      });
  };

  const loadAllSubjects = () => {
    showLoading();
    const query = subjectListAllQuery();
    GraphQL(query, {})
      .then((res) => {
        hideLoading();
        if (res.subjectListAll) {
          res.subjectListAll = res.subjectListAll.map((item) => ({
            ...item,
            label: item.name,
            value: item.id,
          }));
          setSubjects(res.subjectListAll);
        } else {
          showNotificationAutoDestroy(
            'danger',
            t('notifications.error'),
            t('notifications.record-not-found')
          );
          backClickHandler();
        }
      })
      .catch(() => {
        hideLoading();
        showNotificationAutoDestroy(
          'danger',
          t('errors.title'),
          t('errors.error-to-loading')
        );
      });
  };

  useEffect(() => {
    if (match.params.type) {
      dto.type = match.params.type;
      setType(match.params.type);
    } else {
      setType('PLAN');
    }
    if (match.params.id && match.params.id > 0) {
      if (dto.id !== parseInt(match.params.id, 0)) {
        loadData(match.params.id);
      } else {
        setFormOn(true);
      }
    } else {
      setFormOn(true);
    }
  }, [dto]);

  useEffect(() => {
    const { location } = history;
    if (location && location.state) {
      const { state } = location;
      const updatedDto = {
        ...dto,
        rangeDate: {
          initialDate: new Date(state.event.begin),
          finalDate: new Date(state.event.begin),
        },
        discipline: state.event.subject,
        classes: state.event.schoolClass,
      };
      setDto(updatedDto);
    }
  }, [history]);

  useEffect(() => {
    loadRecipients();
    loadAllSubjects();
    loadAllBnccs();
  }, []);

  return (
    <SmartCard title={t('panels.plano-aula')}>
      {formOn && (
        <RecordOccurrencesForm
          onSubmit={handleSubmit}
          backHandler={backClickHandler}
          dto={dto}
          recipients={recipients}
          subjects={subjects}
          bnccs={bnccs}
        />
      )}
    </SmartCard>
  );
};

ClassPlanFormHandler.propTypes = {
  t: PropTypes.func.isRequired,
  match: ReactRouterPropTypes.match.isRequired,
  history: ReactRouterPropTypes.history.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default withTranslation('common')(
  connect((state) => ({
    loading: state.loading,
  }))(ClassPlanFormHandler)
);
